import React, { FC, PropsWithChildren, useEffect, useRef } from 'react';
import { AspectRatio, Card, Divider, Group, Image, Stack, Text } from '@mantine/core';
import { renderNullable, renderOptional } from '@shared/utils/render';
import { IconPhoto } from '@tabler/icons-react';
import * as NEA from 'fp-ts/NonEmptyArray';
import { Pest } from '@modules/diseases/pests/model';
import PestStage from '@modules/diseases/pests/components/PestStage';
import PestDateRange from '@modules/diseases/pests/components/PestDateRange';
import PestAdvice from '@modules/diseases/pests/components/PestAdvice';
import { useLocation } from 'react-router-dom';

interface PestCardProps {
  pest: Pest.Description;
}

const PestCard: FC<PropsWithChildren<PestCardProps>> = ({ pest, children }) => {
  const cardRef = useRef<HTMLDivElement | null>(null);

  const { hash } = useLocation();

  useEffect(() => {
    if (hash === `#${pest.enum}`) {
      cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [hash, pest.enum]);

  return (
    <Card
      ref={cardRef}
      p={20}
      shadow="0px 4px 4px 0px rgba(0, 0, 0, 0.10)"
      sx={theme => ({
        '&[data-with-border]': {
          borderColor: theme.colors.tertiary[2],
        },
      })}
    >
      <Stack spacing={17}>
        <Group noWrap>
          <AspectRatio w="100%" maw={103} ratio={1}>
            <Card radius={10} p={0}>
              {renderNullable(
                pest.image,
                ({ bound_256x256 }) => (
                  <Image src={bound_256x256} />
                ),
                () => (
                  <IconPhoto size={40} strokeWidth={1.4} />
                ),
              )}
            </Card>
          </AspectRatio>
          <Text fw={700} fz={16} lh={1.5}>
            {pest.name}
          </Text>
        </Group>
        <PestDateRange dateRange={pest.dateRange} />
        <Divider />
        {renderOptional(
          NEA.fromArray(pest.stages),
          risks => (
            <Stack>
              {risks.map(({ name, level }, index) => (
                <PestStage key={index} name={name} level={level} />
              ))}
            </Stack>
          ),
          () => (
            <Text c="gray.7" fz={14}>
              Aucun risque de prévu pour cette semaine
            </Text>
          ),
        )}

        {renderNullable(pest.advice, advice => (
          <PestAdvice advice={advice} />
        ))}
        {children}
      </Stack>
    </Card>
  );
};

export default PestCard;
